import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { useSelector } from 'react-redux';

import { IconInviteFriendsSvg } from '../../../assets/settings';
import { facebookInvite } from '../../../services/invite/facebookInvite';
import { goldGlow } from '../../../shared/glowStyles';
import { RootState } from '../../../store';
import { toast } from '../../../utils/helpers/toastUtils';
import { ACHIEVEMENTS_FONT as FONT, GOLD_BUTTON_FILL } from '../Achievements/achievementsTheme';
import { GradientFill } from '../common/GradientFill';
import { Pressable } from '../common/Touchable';
import RedesignModal from './RedesignModal';
import { POPUP, POPUP_TINT, popupBodyStyle, popupColors, PopupHeader } from './RedesignPopup';
import { useRedesignScale } from './useRedesignScale';

/**
 * Invitations, opened from the Settings popup's "Invite friends" row. Drawn as a sibling of
 * "Modal / Settings" (5054:78827): the same panel and header, one surface/raised plate with the
 * row's invite glyph and the pitch, and the gold modal action that shares the invite link.
 */
const PANEL_HEIGHT = 230;
const PLATE = { padding: 12, radius: 12, badge: 52, gap: 12 };
const BUTTON = { height: 47, radius: 10 };

interface InviteModalProps {
	onClose: () => void;
}

function InvitesModal({ onClose }: InviteModalProps) {
	const { t } = useTranslation();
	const myUserId = useSelector((state: RootState) => state.auth.user?.id);
	const scale = useRedesignScale({ fitHeight: PANEL_HEIGHT });
	const styles = useMemo(() => createStyles(scale), [scale]);

	const handleInvite = async () => {
		const traceId = `invite_${Date.now()}`;
		console.log(`📩 [INVITE][${traceId}] CLICK`, { platform: Platform.OS, myUserId });

		if (!myUserId) {
			toast.error('Invite failed', 'No user id. You are not logged in?');
			return;
		}

		const url = `https://svara.app/invite?ref=${myUserId}`;
		try {
			const res = await facebookInvite(url);
			console.log(`📩 [INVITE][${traceId}] SUCCESS: invite shared`, res);
			toast.success('Shared', 'Invite opened');
		} catch (e: any) {
			console.error(`❌ [INVITE][${traceId}] Invite failed`, {
				message: e?.message,
				code: e?.code,
				raw: String(e),
			});
			toast.error('Invite failed', e?.message || 'Try again later');
		}
	};

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			panel={{ height: PANEL_HEIGHT, radius: POPUP.radius, tint: POPUP_TINT, overScreen: true }}
		>
			<View style={styles.body}>
				<PopupHeader
					scale={scale}
					title={t('T_SITE_MODAL_INVITATION_TITLE')}
					onClose={onClose}
					closeLabel={t('T_SITE_A11Y_CLOSE_MODAL', { defaultValue: 'Close modal' })}
				/>

				<View style={styles.plate}>
					<View style={styles.badge}>
						<IconInviteFriendsSvg width={26 * scale} height={31 * scale} />
					</View>
					<Text style={styles.text} numberOfLines={3}>
						{t('T_SITE_MODAL_INVITATION_DESCRIPTION', {
							defaultValue: 'Share your link and play Svara with your friends.',
						})}
					</Text>
				</View>

				<Pressable
					accessibilityRole='button'
					onPress={handleInvite}
					style={({ pressed }) => [styles.button, pressed && styles.buttonPressed]}
				>
					<GradientFill stops={GOLD_BUTTON_FILL} radius={(BUTTON.radius - 1) * scale} />
					<Text style={styles.buttonLabel}>{t('T_SITE_MODAL_INVITATION_INVITE')}</Text>
				</Pressable>
			</View>
		</RedesignModal>
	);
}

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	const hairline = Math.max(1, scale);
	return StyleSheet.create({
		body: popupBodyStyle(scale),
		plate: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(PLATE.gap),
			padding: s(PLATE.padding),
			borderRadius: s(PLATE.radius),
			borderWidth: hairline,
			borderColor: popupColors.plateBorder,
			backgroundColor: popupColors.raised,
		},
		badge: {
			width: s(PLATE.badge),
			height: s(PLATE.badge),
			borderRadius: s(PLATE.badge / 2),
			alignItems: 'center',
			justifyContent: 'center',
			borderWidth: hairline,
			borderColor: popupColors.borderGold,
			backgroundColor: popupColors.sunken,
			...goldGlow,
		},
		text: {
			flex: 1,
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(15),
			lineHeight: s(20),
			color: popupColors.gold50,
		},
		button: {
			height: s(BUTTON.height),
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: s(BUTTON.radius),
			borderWidth: hairline,
			borderColor: popupColors.goldRim,
			...goldGlow,
		},
		buttonPressed: {
			opacity: 0.85,
		},
		buttonLabel: {
			fontFamily: FONT,
			fontWeight: '900',
			fontSize: s(18),
			letterSpacing: s(0.36),
			textTransform: 'uppercase',
			color: popupColors.onGold,
		},
	});
};

export default InvitesModal;
